/**
 * fitColumnWidths — the arithmetic behind every table's `Fit columns to
 * screen` menu item, DOMAIN-FREE.
 *
 * 🔀 **EXTRACTED 2026-09-28 out of `constants/gagTableColumns.js`'s
 * `fitGagColumnWidths`, where it was already parameterised by `columns` and
 * `maxWidth` and carried no gag-specific logic at all — the Breakdown was
 * simply its first caller.** Owner ruling (Pete, 2026-09-28), applying the
 * Breakdown's own 2026-09-27 ruling to every other table: *"reset column
 * widths should use the same code and changes as Fit columns to screen on
 * the Breakdown page. same for other tables."* `constants/gagTableColumns.js`
 * now re-exports `fitGagColumnWidths` as a THIN wrapper over this function
 * with the Breakdown's own defaults (`GAG_TABLE_COLUMNS`,
 * `MAX_GAG_COLUMN_WIDTH`) baked in — never duplicate the algorithm.
 *
 * Given the table's measured `available` width, a column model and a shared
 * ceiling, returns the width map that makes the table exactly `available` px
 * wide — or `null` when there is nothing measured to fit to (the caller then
 * offers nothing — never a guessed width).
 *
 * The column shape is `{ key, width, min, fixed?, fitFloor? }`:
 * - `key`      stable identity, used as the map's key.
 * - `width`    the column's DEFAULT width — every unpinned column scales in
 *              proportion to it.
 * - `min`      the column's floor: the fit never squeezes it narrower, and a
 *              column with no `min` floors at `0` (`Number.isFinite` guard).
 * - `fixed`    an icon/control column the fit never resizes — it keeps its
 *              declared `width` regardless of `available`.
 * - `fitFloor` this column is pinned at its `min`, never scaled — the
 *              Breakdown's per-column choice (*"fit colums to width should
 *              use minium for Status, shoot date, RA"*), offered here as a
 *              column-model FLAG so a caller who wants it opts in per column
 *              rather than the function guessing which columns deserve it.
 *              `fitFloor` wins over `fixed` when a column carries both (the
 *              Breakdown's RA column: `fixed` because it is never
 *              drag-resizable, `fitFloor` because Fit pins it at `min`
 *              explicitly rather than by its default width happening to
 *              equal it).
 *
 * Behaviour:
 * - `fixed` columns (that are not also `fitFloor`) keep their DEFAULT width,
 *   so the result is a function of `available` alone — which is what lets a
 *   caller say "already fits" by comparing this map against the live widths.
 * - `fitFloor` columns are pinned at `min` and never scale.
 * - every other column ("flex") scales in proportion to its DEFAULT width,
 *   never below its `min` and never above `maxWidth`. A column pinned at a
 *   bound drops out and the rest are re-scaled over what is left
 *   (water-filling), so a pinned column's shortfall is spread over the
 *   others rather than lost.
 * - if the flex floors alone exceed the room, every flex column sits at its
 *   floor: the table overflows and the caller's own horizontal-scroll/pan
 *   affordance covers the rest.
 * - integer px; the rounding remainder goes to the largest fractional parts
 *   (column order breaks ties), so the sum is EXACTLY `available` whenever
 *   the bounds allow it. Only when every flex column is at `maxWidth` is it
 *   less — no real screen would need it, and a `w-full` stretch covers it.
 *
 * Pure: no DOM, no storage, no React. The caller saves the result through
 * its own width-setter (`hooks/useColumnWidths.js`'s `setWidth`) — one write
 * path, never a second store.
 *
 * 🔑 **`available` is the table's OWN measured scrollport width, NEVER
 * `window.innerWidth`.** The sidebar, page padding and page scrollbar all
 * come out of the viewport before the table ever sees it; a caller passes
 * `null` when it has not measured yet, and this function returns `null` for
 * anything that is not a positive finite number.
 */
export function fitColumnWidths(available, columns, maxWidth) {
  if (!Number.isFinite(available) || available <= 0) return null;
  const room = Math.floor(available);
  const floorOf = (c) => (Number.isFinite(c.min) ? c.min : 0);
  const out = {};
  let fixedSum = 0;
  for (const c of columns) {
    if (c.fitFloor) { out[c.key] = floorOf(c); fixedSum += floorOf(c); }
    else if (c.fixed) { out[c.key] = c.width; fixedSum += c.width; }
  }
  const flex = columns.filter((c) => !c.fitFloor && !c.fixed);
  const target = room - fixedSum;
  if (flex.reduce((s, c) => s + floorOf(c), 0) >= target) {
    for (const c of flex) out[c.key] = floorOf(c);
    return out;
  }

  // Water-fill: scale the unpinned columns over what the pinned ones leave.
  const pinned = new Map();
  let ideal = {};
  for (let pass = 0; pass <= flex.length; pass += 1) {
    const free = flex.filter((c) => !pinned.has(c.key));
    const left = target - [...pinned.values()].reduce((s, v) => s + v, 0);
    const base = free.reduce((s, c) => s + c.width, 0);
    const scale = base > 0 ? left / base : 0;
    ideal = {};
    let changed = false;
    for (const c of free) {
      const v = c.width * scale;
      if (v < floorOf(c)) { pinned.set(c.key, floorOf(c)); changed = true; }
      else if (v > maxWidth) { pinned.set(c.key, maxWidth); changed = true; }
      else ideal[c.key] = v;
    }
    if (!changed) break;
  }

  for (const [k, v] of pinned) out[k] = v;
  const freeKeys = flex.map((c) => c.key).filter((k) => !pinned.has(k));
  for (const k of freeKeys) out[k] = Math.floor(ideal[k]);
  let short = target - flex.reduce((s, c) => s + out[c.key], 0);
  const byFraction = freeKeys
    .map((k, i) => ({ k, i, f: ideal[k] - Math.floor(ideal[k]) }))
    .sort((a, b) => (b.f - a.f) || (a.i - b.i));
  for (const { k } of byFraction) {
    if (short <= 0) break;
    if (out[k] < maxWidth) { out[k] += 1; short -= 1; }
  }
  return out;
}

/**
 * `areColumnWidthsFit(widths, columns, available, maxWidth)` — the "already
 * fits" comparison the `Fit columns to screen` menu item needs to disable
 * itself. `true` only when `available` is measured AND every column's LIVE
 * width already equals what a fresh fit would produce — so the item is
 * disabled exactly when clicking it would change nothing.
 */
export function areColumnWidthsFit(widths, columns, available, maxWidth) {
  const fitted = fitColumnWidths(available, columns, maxWidth);
  if (fitted == null) return false;
  return columns.every((c) => widths[c.key] === fitted[c.key]);
}
